import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { DIALOG_SHELL, DIALOG_TITLE, FIELD, FIELD_HINT, FIELD_LABEL, FIELD_TRIGGER, PILL_BUTTON } from '@/modules/social-agent/ui-chrome';
import { useCreatePrecondition, useUpdatePrecondition, useToolDefinitions } from '@/hooks';
import { ACCOUNT_WIDE_SCOPE, GLOBAL_PRECONDITION_SCOPE } from '@/services';
import type { PreconditionCombinator, PreconditionOperator, PreconditionSource, ToolPrecondition } from '@/services';
import { PreconditionCombobox, type ComboboxOption } from './precondition-combobox';

interface FieldSuggestion {
	path: string;
	description?: string;
}

/** Walks a JSON Schema's `properties` into dot-paths (e.g. "customer.total_spent"). Descends
 * into nested objects, and transparently through any array's `items` schema — whether the
 * array is a nested property (e.g. `customers: [...]`) or the schema passed in is itself a
 * bare array (a search/list endpoint's `outputSchema` is often `{type: "array", items: {...}}`
 * at the root, not wrapped in an object) — with no path segment added for the unwrap either
 * way. Matches the evaluator's own list-unwrapping (see Brahma's evaluator._resolve_field), so
 * every path this suggests is one the evaluator can actually resolve. */
function extractFieldPaths(schema: unknown, prefix = '', depth = 0): FieldSuggestion[] {
	if (depth > 4 || !schema || typeof schema !== 'object') return [];

	const schemaType = (schema as { type?: string }).type;
	const schemaItems = (schema as { items?: unknown }).items;
	const objectSchema = schemaType === 'array' && schemaItems ? schemaItems : schema;

	const properties = (objectSchema as { properties?: unknown } | undefined)?.properties;
	if (!properties || typeof properties !== 'object') return [];

	const results: FieldSuggestion[] = [];
	for (const [key, rawPropSchema] of Object.entries(properties as Record<string, unknown>)) {
		const propSchema = rawPropSchema as
			| { type?: string; description?: string; items?: unknown }
			| undefined;
		const path = prefix ? `${prefix}.${key}` : key;
		results.push({ path, description: propSchema?.description });
		if (propSchema?.type === 'object' || propSchema?.type === 'array') {
			results.push(...extractFieldPaths(rawPropSchema, path, depth + 1));
		}
	}
	return results;
}

/**
 * "Customer" and "Order" preconditions always read from the same Shopify tools Brahma's own
 * enrichment pipeline already calls at the start of every conversation — customers_search and
 * customers_orders_recent_by_email (see enrich_customer_info/enrich_orders in
 * brahma/engine/enrichment/engine.py) — not a generic customers_get/orders_get. Customer and
 * order truth (purchase history, spend) lives there specifically; a helpdesk's own customer
 * lookup (Zendesk, Gorgias, Zoho, ...) is a different, non-purchase record and not what these
 * mean here. There's exactly one tool per source, so this isn't a choice an admin makes —
 * nothing to pick, nothing to mistype.
 */
const CUSTOMER_CONTEXT_TOOL = { provider: 'shopify', tool: 'customers_search' } as const;
const ORDER_CONTEXT_TOOL = { provider: 'shopify', tool: 'customers_orders_recent_by_email' } as const;

const SOURCES: { value: PreconditionSource; label: string }[] = [
	{ value: 'input', label: 'This action’s own input' },
	{ value: 'customer', label: 'Customer' },
	{ value: 'order', label: 'Order' }
];

const OPERATORS: { value: PreconditionOperator; label: string }[] = [
	{ value: 'eq', label: 'equals' },
	{ value: 'neq', label: 'does not equal' },
	{ value: 'lt', label: 'is less than' },
	{ value: 'lte', label: 'is less than or equal to' },
	{ value: 'gt', label: 'is greater than' },
	{ value: 'gte', label: 'is greater than or equal to' },
	{ value: 'in', label: 'is one of (comma-separated)' },
	{ value: 'exists', label: 'exists' }
];

/** Kept as text: tool args and Shopify fields (ids, amounts) arrive as strings, so a number would never equal them. */
function parseValue(raw: string, operator: PreconditionOperator): string | string[] {
	if (operator === 'in') {
		return raw.split(',').map((v) => v.trim()).filter(Boolean);
	}
	return raw.trim();
}

function stringifyValue(value: unknown): string {
	if (value === undefined) return '';
	if (Array.isArray(value)) return value.join(', ');
	if (typeof value === 'string') return value;
	return JSON.stringify(value);
}

interface PreconditionFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	provider: string;
	tool: string;
	/** The group's current combinator — carried onto a newly-created row so every row in the
	 * group agrees from the moment it's added (only a new row needs this; editing an existing
	 * one never changes which group it's in). */
	combinator: PreconditionCombinator;
	editing?: ToolPrecondition | null;
}

/** Add/edit dialog for one precondition — a deterministic check gating this action's writes. */
export function PreconditionFormDialog({
	open,
	onOpenChange,
	provider,
	tool,
	combinator,
	editing
}: PreconditionFormDialogProps) {
	const isEditing = !!editing;
	// Account-wide rules can't read an action's input: every action takes different input.
	const isGlobal = tool === GLOBAL_PRECONDITION_SCOPE;
	const sources = isGlobal ? SOURCES.filter((s) => s.value !== 'input') : SOURCES;
	const defaultSource: PreconditionSource = isGlobal ? 'customer' : 'input';
	const createMutation = useCreatePrecondition();
	const updateMutation = useUpdatePrecondition();
	const isSaving = createMutation.isPending || updateMutation.isPending;

	const { data: allTools } = useToolDefinitions();
	const providerTools = useMemo(
		() => (allTools ?? []).filter((t) => t.provider.toLowerCase() === provider.toLowerCase()),
		[allTools, provider]
	);
	const currentAction = useMemo(() => providerTools.find((t) => t.tool === tool), [providerTools, tool]);

	// The account's real customers_search/customers_orders_recent_by_email tool definitions,
	// if Shopify is connected —
	// undefined means that source isn't available on this account. Brahma stores every tool
	// result under a "provider:tool" key (see react.py's _store_tool_result_in_vars) and, if
	// that key isn't already populated this turn, calls the tool for real to fill it in before
	// evaluating — so sourceTool is that same "provider:tool" key, not a bare tool name.
	const customerTool = useMemo(
		() =>
			(allTools ?? []).find(
				(t) => t.provider.toLowerCase() === CUSTOMER_CONTEXT_TOOL.provider && t.tool === CUSTOMER_CONTEXT_TOOL.tool
			),
		[allTools]
	);
	const orderTool = useMemo(
		() =>
			(allTools ?? []).find(
				(t) => t.provider.toLowerCase() === ORDER_CONTEXT_TOOL.provider && t.tool === ORDER_CONTEXT_TOOL.tool
			),
		[allTools]
	);

	const [source, setSource] = useState<PreconditionSource>(defaultSource);
	const [field, setField] = useState('');
	const [operator, setOperator] = useState<PreconditionOperator>('eq');
	const [value, setValue] = useState('');
	const [description, setDescription] = useState('');

	const resolvedSourceTool = source === 'customer' ? customerTool : source === 'order' ? orderTool : undefined;
	const sourceToolKey =
		source === 'customer'
			? `${CUSTOMER_CONTEXT_TOOL.provider}:${CUSTOMER_CONTEXT_TOOL.tool}`
			: source === 'order'
				? `${ORDER_CONTEXT_TOOL.provider}:${ORDER_CONTEXT_TOOL.tool}`
				: '';

	// Field suggestions come from whichever tool's output this precondition actually reads:
	// the write action's own input schema for "input", or the fixed source tool's output
	// schema for "customer"/"order".
	const fieldSuggestions = useMemo(() => {
		if (source === 'input') return extractFieldPaths(currentAction?.inputSchema);
		return extractFieldPaths(resolvedSourceTool?.outputSchema);
	}, [source, currentAction, resolvedSourceTool]);

	const fieldComboOptions: ComboboxOption[] = useMemo(
		() => fieldSuggestions.map((f) => ({ value: f.path, label: f.path, description: f.description })),
		[fieldSuggestions]
	);

	useEffect(() => {
		if (!open) return;
		setSource(editing?.source ?? defaultSource);
		setField(editing?.field ?? '');
		setOperator(editing?.operator ?? 'eq');
		setValue(editing ? stringifyValue(editing.value) : '');
		setDescription(editing?.description ?? '');
	}, [open, editing, defaultSource]);

	const handleSave = async () => {
		if (!field.trim()) {
			toast.error('Field is required');
			return;
		}
		if (source !== 'input' && !resolvedSourceTool) {
			toast.error("This account doesn't have Shopify connected, so this source isn't available");
			return;
		}
		if (operator !== 'exists' && !value.trim()) {
			toast.error('Value is required unless the operator is "exists"');
			return;
		}

		const payload = {
			provider,
			tool,
			// Only meaningful on create — carries the group's current combinator onto a new
			// row so it never disagrees with the rest of the group. update() never sends
			// this; combinator changes only ever go through the section's All/Any toggle.
			...(isEditing ? {} : { combinator }),
			source,
			sourceTool: source === 'input' ? undefined : sourceToolKey,
			field: field.trim(),
			operator,
			value: operator === 'exists' ? undefined : parseValue(value, operator),
			description: description.trim() || undefined
		};

		try {
			if (isEditing) {
				await updateMutation.mutateAsync({ id: editing.id, provider, tool, data: payload });
				toast.success('Precondition updated');
			} else {
				await createMutation.mutateAsync(payload);
				toast.success('Precondition added');
			}
			onOpenChange(false);
		} catch {
			toast.error('Failed to save precondition');
		}
	};

	const selectTrigger = cn('w-full text-xs data-[size=default]:h-10 md:text-xs', FIELD_TRIGGER);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className={cn('w-full max-w-[480px] bg-white', DIALOG_SHELL)}>
				<DialogHeader className='h-[50px] justify-center border-b border-border px-3 py-2'>
					<DialogTitle className={DIALOG_TITLE}>{isEditing ? 'Edit precondition' : 'Add precondition'}</DialogTitle>
				</DialogHeader>

				<div className='flex max-h-[620px] flex-col gap-6 overflow-auto px-4 py-6'>
					<DialogDescription className={FIELD_HINT}>
						{isGlobal
							? `${ACCOUNT_WIDE_SCOPE} If it fails, the action goes to human approval instead.`
							: "Checked before this action runs automatically. If it fails, the action goes to human approval instead, whatever the action's own risk level."}
					</DialogDescription>

					<div className='flex flex-col gap-2'>
						<Label htmlFor='precondition-source' className={FIELD_LABEL}>
							Check against
						</Label>
						<Select value={source} onValueChange={(v) => setSource(v as PreconditionSource)}>
							<SelectTrigger id='precondition-source' className={selectTrigger}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{sources.map((s) => (
									<SelectItem
										key={s.value}
										value={s.value}
										disabled={(s.value === 'customer' && !customerTool) || (s.value === 'order' && !orderTool)}
									>
										{s.label}
										{((s.value === 'customer' && !customerTool) || (s.value === 'order' && !orderTool)) &&
											' (Shopify not connected)'}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{source !== 'input' && (
							<p className={FIELD_HINT}>
								Reads Shopify's{' '}
								{source === 'customer' ? 'customers_search' : 'customers_orders_recent_by_email'}, called directly
								if it hasn't already run earlier in the conversation.
							</p>
						)}
					</div>

					<div className='flex flex-col gap-2'>
						<Label htmlFor='precondition-field' className={FIELD_LABEL}>
							Field
						</Label>
						<PreconditionCombobox
							id='precondition-field'
							value={field}
							onChange={setField}
							options={fieldComboOptions}
							placeholder={source === 'input' ? 'e.g. refundAmount' : 'e.g. customer.total_spent'}
							searchPlaceholder='Search fields...'
							emptyText={source !== 'input' && !resolvedSourceTool ? "Shopify isn't connected" : 'No fields found'}
						/>
					</div>

					<div className='flex flex-col gap-2'>
						<Label htmlFor='precondition-operator' className={FIELD_LABEL}>
							Operator
						</Label>
						<Select value={operator} onValueChange={(v) => setOperator(v as PreconditionOperator)}>
							<SelectTrigger id='precondition-operator' className={selectTrigger}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{OPERATORS.map((o) => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{operator !== 'exists' && (
						<div className='flex flex-col gap-2'>
							<Label htmlFor='precondition-value' className={FIELD_LABEL}>
								Value
							</Label>
							<Input
								id='precondition-value'
								className={FIELD}
								value={value}
								onChange={(e) => setValue(e.target.value)}
								placeholder={operator === 'in' ? 'e.g. vip, wholesale' : 'e.g. 1000'}
							/>
						</div>
					)}

					<div className='flex flex-col gap-2'>
						<Label htmlFor='precondition-description' className={FIELD_LABEL}>
							Description (shown to the reviewer)
						</Label>
						<Input
							id='precondition-description'
							className={FIELD}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder='e.g. High-spend customers require human review'
						/>
					</div>
				</div>

				<div className='flex items-center justify-between p-3'>
					<Button variant='outline' className={PILL_BUTTON} onClick={() => onOpenChange(false)} disabled={isSaving}>
						Cancel
					</Button>
					<Button className={PILL_BUTTON} onClick={handleSave} disabled={isSaving}>
						{isEditing ? 'Save changes' : 'Add precondition'}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
